import React, { useEffect, useState } from 'react'
import { Alert, Button, Paper, Snackbar, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import PageHeader from '../PageHeader'
import AdminSearch from './AdminSearch'
import GroupSearch from './GroupSearch'
import PermissionPicker from './PermissionPicker'
import { buildPermissions, initialLevels } from './permissionsConfig'
import { groupCreationService } from '../../services/groupCreationService'
import ReactGA from 'react-ga4'

const AddGroupAdmin = () => {
  const { groupId } = useParams()
  const navigate = useNavigate()

  const [selectedGroup, setSelectedGroup] = useState(null)
  const [selectedAdmin, setSelectedAdmin] = useState(null)
  const [levels, setLevels] = useState(initialLevels)

  const [submitting, setSubmitting] = useState(false)
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' })

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: window?.location?.pathname ?? '',
      title: 'Add Group Admin Page'
    })
  }, [])

  const setLevel = (domainKey, level) => {
    setLevels(prev => ({ ...prev, [domainKey]: level }))
  }

  const isValid = selectedGroup && selectedAdmin

  const submit = async () => {
    if (!isValid) return
    setSubmitting(true)
    try {
      const res = await groupCreationService.addAdmin(groupId, {
        targetGroupId: selectedGroup.id,
        adminUserId: selectedAdmin.id,
        permissions: buildPermissions(levels)
      })
      setToast({ open: true, severity: 'success', message: res.message || 'Admin added' })
      setSelectedAdmin(null)
      setLevels(initialLevels)
    } catch (err) {
      const message = err?.response?.data?.message || 'There was an error adding the group admin'
      setToast({ open: true, severity: 'error', message })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <PageHeader title='Add a Group Admin' />
      <Paper className='p-6 max-w-2xl mx-auto' elevation={1}>
        <Typography variant='h6' className='mb-2'>Group</Typography>
        <GroupSearch
          groupId={groupId}
          selected={selectedGroup}
          onSelect={setSelectedGroup}
          onClear={() => setSelectedGroup(null)}
        />

        <Typography variant='h6' className='mt-4 mb-2'>Admin</Typography>
        <AdminSearch
          groupId={groupId}
          selected={selectedAdmin}
          onSelect={setSelectedAdmin}
          onClear={() => setSelectedAdmin(null)}
        />

        <Typography variant='h6' className='mt-4 mb-2'>Admin permissions</Typography>
        <PermissionPicker levels={levels} onChange={setLevel} />

        <div className='flex justify-end gap-3 mt-6'>
          <Button onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant='contained' disabled={!isValid || submitting} onClick={submit}>
            {submitting ? 'Adding…' : 'Add Admin'}
          </Button>
        </div>
      </Paper>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default AddGroupAdmin
